import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { DateTime } from 'luxon'
import z from 'zod'

const isoDate = z.string().date()
export type DateOnly = z.infer<typeof isoDate>

/**
 * @deprecated Use useLocalizedDate instead
 */
export function getFormattedDate(date: Date | string) {
  const parsedDate = typeof date === 'string' ? new Date(date) : date
  return format(parsedDate, 'MMM dd, yyyy, h:mm a')
}

export function getFormattedTimestamp(date?: Date | string) {
  const parsedDate = date ? (typeof date === 'string' ? new Date(date) : date) : new Date()
  return format(parsedDate, 'yyyyMMddHHmm')
}

export function getNextWeekFriday(today = new Date()) {
  const dayOfWeek = today.getDay()

  // Get to next week's Sunday
  const daysUntilNextWeek = 7 - dayOfWeek
  const nextWeekSunday = new Date(today)
  nextWeekSunday.setDate(today.getDate() + daysUntilNextWeek)

  // Add 5 days to get to Friday
  const nextWeekFriday = new Date(nextWeekSunday)
  nextWeekFriday.setDate(nextWeekSunday.getDate() + 5)

  return nextWeekFriday
}

export function getUtcRangeForDate(localDate: string, timeZone: string) {
  const start = DateTime.fromISO(`${localDate}T00:00:00`, { zone: timeZone }).toUTC()
  const end = start.plus({ days: 1 })

  return {
    start: start.toISO(),
    end: end.toISO(),
  }
}

export function getYearWeekNumber(date: Date) {
  return formatInTimeZone(date, 'UTC', 'RRRRII')
}

export function formatDateDdMmmYyyy(date: Date | null): string | null {
  if (!date) return null
  return formatInTimeZone(date, 'UTC', 'dd-MMM-yyyy')
}

/**
 * Returns the first day of the month containing `date`, expressed in the given
 * IANA timezone, as a YYYY-MM-01 string. Use this when the month boundary is
 * defined by a business timezone rather than the server's local clock.
 *
 * @example
 * // April 30, 2025 at 10 PM Toronto = May 1, 02:00 UTC
 * formatMonthStartInTimeZone(new Date('2025-05-01T02:00:00Z'), 'America/Toronto')
 * // '2025-04-01'
 */
export function formatMonthStartInTimeZone(date: Date, timeZone: string = 'UTC'): DateOnly {
  return formatInTimeZone(date, timeZone, 'yyyy-MM-01') as DateOnly
}

/** Day of the month (1–31) of `date` in the given IANA timezone. */
export function getDayOfMonthInTimeZone(date: Date, timeZone: string = 'UTC'): number {
  return Number(formatInTimeZone(date, timeZone, 'd'))
}
